import React, { useState, useEffect } from "react";
import { Form, Input } from "semantic-ui-react";
import { Box, Button, CircularProgress, Typography, Alert } from "@mui/material";
import { Link, useNavigate, useParams } from "react-router-dom";
import PageTitle from "../../components/PageTitle/PageTitle";
import customHttp from "../../services/customHTTP"; // Assuming you have a custom HTTP utility for API calls
import "./ResetPassword.css";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { ASSETS_BASE_URL } from "../../config";

const ResetPassword = () => {
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [loading, setLoading] = useState(false);
	const [combinedErrorMessage, setCombinedErrorMessage] = useState("");
	const [passwordError, setPasswordError] = useState("");
	const [confirmPasswordError, setConfirmPasswordError] = useState("");
	const [successMessage, setSuccessMessage] = useState("");
	const [validation, setValidation] = useState({
		minLength: false,
		uppercase: false,
		specialChar: false,
		number: false,
		confirmPass: false,
	});
	const navigate = useNavigate();

	const { token } = useParams(); // Get token from URL

	useEffect(() => {
		if (!token) {
			setCombinedErrorMessage("Invalid or missing token.");
		}
	}, [token]);

	const handleResetPassword = async (e) => {
		e.preventDefault();
		setLoading(true);
		setPasswordError("");
		setConfirmPasswordError("");
		setCombinedErrorMessage("");

		// Validate password match
		if (password !== confirmPassword) {
			setCombinedErrorMessage("Passwords do not match.");
			setLoading(false);
			return;
		}

		// Prepare request body
		const req_body = {
			token,
			password,
		};

		try {
			// Make API call to reset password
			const response = await customHttp.post("/auth/reset-password", req_body);

			if (response.status === 200) {
				setSuccessMessage("Password has been reset");
				navigate("/login");
			} else {
				setCombinedErrorMessage("An error occurred while resetting the password.");
			}
		} catch (error) {
			setCombinedErrorMessage("Error resetting password. Please try again.");
		}

		setLoading(false);
	};

	const validatePassword = (password) => {
		const minLength = password.length >= 8;
		const uppercase = /[A-Z]/.test(password);
		const specialChar = /[!@#$%^&*]/.test(password);
		const number = /[0-9]/.test(password);

		setValidation({
			minLength,
			uppercase,
			specialChar,
			number,
		});
	};

	const validateConfirmPassword = (confirmPasswd) => {
		const confirmpass = password === confirmPasswd;
		setValidation({
			...validation,
			confirmpass,
		});
	};

	const handlePasswordChange = (e) => {
		const newPassword = e.target.value;
		setPassword(newPassword);
		validatePassword(newPassword);
	};

	const handleConfirmPasswordChange = (e) => {
		setConfirmPassword(e.target.value);
		validateConfirmPassword(e.target.value);
	};

	const isFormValid = () => {
		return (
			validation.minLength &&
			validation.uppercase &&
			validation.specialChar &&
			validation.number &&
			password === confirmPassword
		);
	};

	const AuthPageStyles = {
		backgroundImage: `url('${ASSETS_BASE_URL}/home_bg2.jpg')`,
		backgroundSize: "cover",
		backgroundPosition: "center",
	};

	return (
		<Box className="login-container" sx={AuthPageStyles}>
			<Box className="login-form-container">
				<img src="/logo.png" alt="Logo" className="login-logo" />
				<div className="form-container">
					<PageTitle title="Reset Password" />

					{/* Display the error alert on top of the form */}
					{combinedErrorMessage && (
						<Alert severity="error" sx={{ width: "100%", marginBottom: "0", marginTop: "15px" }}>
							{combinedErrorMessage}
						</Alert>
					)}

					<Form className="login-form" onSubmit={handleResetPassword} error={!!combinedErrorMessage}>
						<Form.Field error={!!passwordError}>
							<Input
								fluid
								placeholder="New Password"
								type="password"
								className="login-input"
								value={password}
								onChange={handlePasswordChange}
							/>
						</Form.Field>

						<Form.Field error={!!confirmPasswordError}>
							<Input
								fluid
								placeholder="Confirm New Password"
								type="password"
								className="login-input"
								value={confirmPassword}
								onChange={handleConfirmPasswordChange}
							/>
						</Form.Field>

						<div className="password-validation" style={{ marginBottom: "15px" }}>
							<p
								style={{
									fontSize: "11px",
									color: validation.minLength ? "green" : "#aaa",
									marginBottom: 0,
								}}>
								{validation.minLength ? (
									<CheckCircleIcon fontSize="11px" />
								) : (
									<ErrorOutlineIcon fontSize="11px" />
								)}{" "}
								Minimum 8 characters
							</p>
							<p
								style={{
									fontSize: "11px",
									color: validation.uppercase ? "green" : "#aaa",
									marginBottom: 0,
								}}>
								{validation.uppercase ? (
									<CheckCircleIcon fontSize="11px" />
								) : (
									<ErrorOutlineIcon fontSize="11px" />
								)}{" "}
								At least one uppercase letter
							</p>
							<p
								style={{
									fontSize: "11px",
									color: validation.specialChar ? "green" : "#aaa",
									marginBottom: 0,
								}}>
								{validation.specialChar ? (
									<CheckCircleIcon fontSize="11px" />
								) : (
									<ErrorOutlineIcon fontSize="11px" />
								)}{" "}
								At least one special character
							</p>
							<p
								style={{
									fontSize: "11px",
									color: validation.number ? "green" : "#aaa",
									marginBottom: 0,
								}}>
								{validation.number ? (
									<CheckCircleIcon fontSize="11px" />
								) : (
									<ErrorOutlineIcon fontSize="11px" />
								)}{" "}
								At least one number
							</p>
							<p
								style={{
									fontSize: "11px",
									color: validation.confirmpass ? "green" : "#aaa",
									marginBottom: 0,
								}}>
								{validation.confirmpass ? (
									<CheckCircleIcon fontSize="11px" />
								) : (
									<ErrorOutlineIcon fontSize="11px" />
								)}{" "}
								Confirm Password Matches
							</p>
						</div>

						<div className="login-button">
							<Button
								variant="contained"
								color="primary"
								className="button-primary"
								type="submit"
								sx={{ minWidth: "120px" }}
								disabled={!isFormValid()}>
								{loading ? "Resetting..." : "Reset Password"}
							</Button>
						</div>
					</Form>

					{/* Verifying spinner below the button */}
					{loading && (
						<Box display="flex" alignItems="center" justifyContent="center" mt={2}>
							<CircularProgress size={24} sx={{ color: "green" }} />
							<Typography variant="body2" sx={{ marginLeft: 2, color: "green" }}>
								Verifying...
							</Typography>
						</Box>
					)}
				</div>
				{successMessage && <p style={{ color: "green" }}>{successMessage}</p>}
				<Link to="/login" className="login-link">
					Back to Login
				</Link>
			</Box>
		</Box>
	);
};

export default ResetPassword;
